/**
 * Copy.
 *
 * Arabic is the source, not a translation target (§1). English exists so the
 * developer's own team can read the interface; every phrase was written in
 * Arabic first and the English follows it.
 *
 * Two rules from §7 are enforced here rather than left to whoever types the
 * next string: buttons say what happens («اطلب السعر», not «إرسال»), and empty
 * or error states give direction instead of apologising.
 */

export type Locale = 'ar' | 'en';

export const LOCALES: readonly Locale[] = ['ar', 'en'];

export function directionOf(locale: Locale): 'rtl' | 'ltr' {
  return locale === 'ar' ? 'rtl' : 'ltr';
}

const ar = {
  'brand.alt': 'تكريس',
  'brand.marketedBy': 'تسويق',

  'nav.skip': 'تخطَّ إلى محتوى المشروع',
  'nav.backToMasterplan': 'العودة إلى المخطط',
  'nav.viewport': 'نموذج المشروع ثلاثي الأبعاد',
  'nav.viewportHelp':
    'اسحب للدوران، قرّب بإصبعين أو بعجلة الفأرة. اضغط على وحدة لعرض تفاصيلها.',

  'action.requestPrice': 'اطلب السعر',
  'action.whatsapp': 'تواصل واتساب',
  'action.call': 'اتصال',
  'action.close': 'إغلاق',
  'action.send': 'أرسل الطلب',
  'action.sending': 'جارٍ الإرسال…',
  'action.retry': 'أعد المحاولة',
  'action.day': 'نهار',
  'action.night': 'ليل',
  'action.lighting': 'الإضاءة',
  'action.language': 'English',

  'layer.title': 'طبقات التنفيذ',
  'layer.empty': 'اختر طبقة لعرض الضمان المرتبط بها',
  'layer.warrantyLabel': 'الضمان',
  'layer.none': 'لا يوجد ضمان مرتبط بهذه الطبقة',

  'legend.title': 'حالة الوحدات',
  'legend.hint': 'اللون على النموذج يطابق هذا المفتاح',

  'status.available': 'متاحة',
  'status.reserved': 'محجوزة',
  'status.sold': 'مباعة',

  'unit.villa': 'فيلا',
  'unit.townhouse': 'تاون هاوس',
  'dwelling.title': 'الوحدات في هذا المبنى',
  'dwelling.hint': 'هذا المبنى يضم وحدتين مستقلتين. اختر الوحدة أولًا.',
  'dwelling.separateEntrance': 'مدخل مستقل',
  'dwelling.priceOnRequest': 'السعر عند الطلب',

  'level.title': 'الأدوار',
  'level.all': 'كامل الوحدة',

  'fact.area': 'المساحة',
  'fact.areaBasis': 'أساس المساحة',
  'fact.areaBasisTbc': 'تحت التأكيد',
  'fact.street': 'عرض الشارع',
  'fact.orientation': 'الواجهة',
  'fact.bathrooms': 'دورات المياه',
  'fact.metres': 'م',
  'fact.squareMetres': 'م²',
  'fact.unknown': '—',

  'rooms.title': 'المكونات',
  'warranties.title': 'الضمانات',
  'highlights.title': 'لماذا هذا المشروع',
  'location.title': 'الموقع',

  'form.title': 'اطلب السعر',
  'form.lede': 'اترك رقمك ونعاود التواصل معك بتفاصيل الوحدة والسعر.',
  'form.name': 'الاسم',
  'form.namePlaceholder': 'الاسم الكامل',
  'form.phone': 'رقم الجوال',
  'form.phonePlaceholder': '05XXXXXXXX',
  'form.phoneHint': 'رقم سعودي. نستخدمه للتواصل بخصوص هذا الطلب فقط.',
  'form.interest': 'الوحدة المطلوبة',
  'form.message': 'سؤالك (اختياري)',
  'form.consent':
    'أوافق على التواصل معي عبر الهاتف أو الواتساب بخصوص هذا الطلب.',
  'form.consentRequired': 'نحتاج موافقتك قبل حفظ رقمك.',
  'form.success':
    'وصلنا طلبك. سنتواصل معك على الرقم الذي أدخلته. رقمك المرجعي:',
  'form.errorGeneric': 'تعذر إرسال الطلب. تحقق من الاتصال وأعد المحاولة.',
  'form.nameShort': 'اكتب الاسم كاملًا',
  'form.phoneInvalid': 'رقم جوال سعودي غير صحيح',
  'form.offline': 'لا يوجد اتصال. استخدم واتساب أو الاتصال المباشر.',

  'loading.masterplan': 'جارٍ تحميل المخطط',
  'loading.unit': 'جارٍ تحميل الوحدة',

  'action.panorama': 'جولة 360°',
  'pano.loading': 'جارٍ تحميل الجولة',
  'pano.error': 'تعذر تحميل الجولة. تحقق من الاتصال وأعد المحاولة.',
  'pano.hint': 'اسحب للنظر حولك',

  'fallback.title': 'لا يمكن عرض النموذج ثلاثي الأبعاد على هذا الجهاز',
  'fallback.body':
    'متصفحك لا يدعم WebGL 2. بيانات المشروع كاملة أدناه، ويمكنك طلب السعر مباشرة.',

  'notice.provisional': 'تخطيط مبدئي',
  'notice.licenceMissing':
    'رقم الترخيص الإعلاني وتسجيل «وافي» غير متوفرين بعد.',
  'notice.demo': 'نسخة تجريبية — بيانات افتراضية',
  'price.demo': 'سعر افتراضي',
  'pano.demo': 'مشهد افتراضي للعرض',

  'hero.cta': 'استكشف المخطط التفاعلي',
  'hero.alt': 'لقطة جوية للمشروع وقت الغروب',
  'hero.demoImage': 'صورة معالجة للعرض — التوزيع من النموذج الفعلي',
  'hero.hint': 'نموذج ثلاثي الأبعاد حي: تجوّل، افتح الوحدات، واعرض الأدوار',

  'placeholder.tier':
    'النموذج المعروض تخطيطي مبدئي، ويُستبدل بالنموذج المعماري المعتمد دون تغيير في الواجهة.',
} as const;

export type MessageKey = keyof typeof ar;

const en: Record<MessageKey, string> = {
  'brand.alt': 'TQRES',
  'brand.marketedBy': 'Marketed by',

  'nav.skip': 'Skip to project content',
  'nav.backToMasterplan': 'Back to the masterplan',
  'nav.viewport': '3D project model',
  'nav.viewportHelp':
    'Drag to orbit, pinch or scroll to zoom. Tap a unit for its details.',

  'action.requestPrice': 'Request the price',
  'action.whatsapp': 'WhatsApp us',
  'action.call': 'Call',
  'action.close': 'Close',
  'action.send': 'Send request',
  'action.sending': 'Sending…',
  'action.retry': 'Try again',
  'action.day': 'Day',
  'action.night': 'Night',
  'action.lighting': 'Lighting',
  'action.language': 'العربية',

  'layer.title': 'Build layers',
  'layer.empty': 'Pick a layer to see the warranty behind it',
  'layer.warrantyLabel': 'Warranty',
  'layer.none': 'No warranty is attached to this layer',

  'legend.title': 'Unit availability',
  'legend.hint': 'Colours on the model match this key',

  'status.available': 'Available',
  'status.reserved': 'Reserved',
  'status.sold': 'Sold',

  'unit.villa': 'Villa',
  'unit.townhouse': 'Townhouse',
  'dwelling.title': 'Homes in this building',
  'dwelling.hint': 'This building holds two independent homes. Choose one.',
  'dwelling.separateEntrance': 'Separate entrance',
  'dwelling.priceOnRequest': 'Price on request',

  'level.title': 'Levels',
  'level.all': 'Whole unit',

  'fact.area': 'Area',
  'fact.areaBasis': 'Area basis',
  'fact.areaBasisTbc': 'To be confirmed',
  'fact.street': 'Street width',
  'fact.orientation': 'Frontage',
  'fact.bathrooms': 'Bathrooms',
  'fact.metres': 'm',
  'fact.squareMetres': 'm²',
  'fact.unknown': '—',

  'rooms.title': 'Rooms',
  'warranties.title': 'Warranties',
  'highlights.title': 'Why this project',
  'location.title': 'Location',

  'form.title': 'Request the price',
  'form.lede': 'Leave your number and we will come back with unit and pricing details.',
  'form.name': 'Name',
  'form.namePlaceholder': 'Full name',
  'form.phone': 'Mobile number',
  'form.phonePlaceholder': '05XXXXXXXX',
  'form.phoneHint': 'Saudi number. Used only to answer this request.',
  'form.interest': 'Unit of interest',
  'form.message': 'Your question (optional)',
  'form.consent': 'I agree to be contacted by phone or WhatsApp about this request.',
  'form.consentRequired': 'We need your consent before storing your number.',
  'form.success': 'Request received. We will call the number you entered. Reference:',
  'form.errorGeneric': 'The request could not be sent. Check your connection and try again.',
  'form.nameShort': 'Enter your full name',
  'form.phoneInvalid': 'Not a valid Saudi mobile number',
  'form.offline': 'You are offline. Use WhatsApp or call directly.',

  'loading.masterplan': 'Loading the masterplan',
  'loading.unit': 'Loading the unit',

  'action.panorama': '360° tour',
  'pano.loading': 'Loading the tour',
  'pano.error': 'The tour could not load. Check your connection and try again.',
  'pano.hint': 'Drag to look around',

  'fallback.title': 'This device cannot show the 3D model',
  'fallback.body':
    'Your browser does not support WebGL 2. The full project details are below, and you can request the price directly.',

  'notice.provisional': 'Provisional layout',
  'notice.licenceMissing':
    'The advertising licence number and Wafi registration are not yet available.',
  'notice.demo': 'Demo build — fictitious data',
  'price.demo': 'Demo price',
  'pano.demo': 'Generated demo scene',

  'hero.cta': 'Explore the interactive masterplan',
  'hero.alt': 'Aerial view of the compound at golden hour',
  'hero.demoImage': 'Enhanced demo image — layout from the actual model',
  'hero.hint': 'A live 3D model: orbit, open units, isolate floors',

  'placeholder.tier':
    'The model shown is provisional massing. It is replaced by the approved architectural model with no change to this interface.',
};

const DICTIONARIES: Record<Locale, Record<MessageKey, string>> = { ar, en };

export function translate(locale: Locale, key: MessageKey): string {
  return DICTIONARIES[locale][key];
}

/**
 * Arabic year agreement. The brochure writes «15 سنة» and «10 سنوات» and both
 * are correct — the rule changes at 3 and again at 11.
 */
export function formatYears(locale: Locale, years: number): string {
  if (locale === 'en') return `${years} ${years === 1 ? 'year' : 'years'}`;
  if (years === 1) return 'سنة واحدة';
  if (years === 2) return 'سنتان';
  if (years <= 10) return `${years} سنوات`;
  return `${years} سنة`;
}

/** Just the noun, for callouts that render the numeral separately and larger. */
export function yearsNoun(locale: Locale, years: number): string {
  if (locale === 'en') return years === 1 ? 'year' : 'years';
  if (years === 1) return 'سنة';
  if (years === 2) return 'سنتان';
  if (years <= 10) return 'سنوات';
  return 'سنة';
}

/**
 * Prices are Western numerals with Latin grouping in both locales (§3) — never
 * ٠١٢٣ beside 0123 in the same interface.
 */
export function formatPrice(locale: Locale, sar: number): string {
  const digits = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(sar);
  return locale === 'ar' ? `${digits} ريال` : `SAR ${digits}`;
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}
